import React, { useState, useEffect, useCallback } from "react";
import { Text, Img } from "components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ProductCartCardBox from "components/ProductCartCardBox";
import globalRequest from "../../../../utils/global/globalRequest";
import { useDispatch, useSelector } from "react-redux";
import { changeLoader } from "../../../../redux/reducers/loader";
import { setSnackbar } from "../../../../redux/reducers/snackbar";
import * as CUSTOM from "../../../../utils/helper/custom";
import * as APIS from "../../../../utils/helper/Enum";
import { currentTrigger } from "redux/reducers/trigger";
import { getAuth } from "../../../../redux/reducers/loginData";

const WishList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector(getAuth);
  const loaderStatus = useSelector(currentTrigger);

  const [wishlistItems, setWishlistItems] = useState([]);
  const [apiLoadStatus, setApiLoadStatus] = useState(true);
  const [cartData, setCartData] = useState([]);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [inputData] = useState({ page: 1, limit: 100 });

  /**
   * Fetch wishlist from API
   */
  const getWishlist = useCallback(async () => {
    dispatch(changeLoader(true));
    try {
      const res = await globalRequest(
        APIS?.WISHLIST?.GET_WISHLIST,
        "get",
        {},
        { params: inputData },
        true
      );
      const data = res?.data;
      if (data?.status === "SUCCESS") {
        const items = data?.data?.data || [];
        setWishlistItems(items);

        const ids = new Set(items.map((item) => item?.productId));
        setWishlistIds(ids);
      } else {
        setWishlistItems([]);
      }
    } catch (e) {
      setWishlistItems([]);
    }
    setApiLoadStatus(false);
    dispatch(changeLoader(false));
  }, [dispatch, inputData]);

  /**
   * Toggle wishlist (remove from wishlist when heart clicked)
   */
  const handleWishlistToggle = async (productId) => {
    if (!auth?.id) {
      dispatch(
        setSnackbar({
          snackbarOpen: true,
          snackbarMessage: "Please login to proceed",
          snackbarState: "error",
        })
      );
      return;
    }

    const wasWishlisted = wishlistIds.has(productId);
    if (wasWishlisted) {
      setWishlistItems((prev) =>
        prev.filter((item) => item?.productId !== productId)
      );
      setWishlistIds((prev) => {
        const updated = new Set(prev);
        updated.delete(productId);
        return updated;
      });
    }

    try {
      const res = await globalRequest(
        APIS?.WISHLIST?.ADD_TO_WISHLIST,
        "post",
        { productId },
        {},
        true
      );
      const data = res?.data;
      if (data?.status === "SUCCESS") {
        const isNowWishlisted = data?.data?.isWishlist;

        if (isNowWishlisted) {
          setWishlistIds((prev) => {
            const updated = new Set(prev);
            updated.add(productId);
            return updated;
          });
        }

        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: data?.message,
            snackbarState: "success",
          })
        );
      } else {
        if (wasWishlisted) {
          getWishlist();
        }
        dispatch(
          setSnackbar({
            snackbarOpen: true,
            snackbarMessage: data?.message,
            snackbarState: "error",
          })
        );
      }
    } catch (e) {
      if (wasWishlisted) {
        getWishlist();
      }
      console.error("Wishlist toggle error:", e);
    }
  };

  useEffect(() => {
    getWishlist();
  }, []);

  useEffect(() => {
    setCartData(CUSTOM.getCartData());
  }, []);

  useEffect(() => {
    if (loaderStatus === "loadCartData") {
      setCartData(CUSTOM.getCartData());
    }
  }, [loaderStatus]);

  // ─── Empty State ────────────────────────────────────────────────
  if (!apiLoadStatus && wishlistItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 w-full min-h-[60vh]">
        <div className="mb-8">
          <img
            src="/images/NowishlistHeart.png"
            alt="empty wishlist"
            className="w-[218px] h-[226px] object-contain"
          />
        </div>

        <Text
          className="text-[32px] font-semibold text-[#1A1A1A] font-['Unbounded'] leading-[48px] text-center mb-2"
        >
          {t("noItemsInWishlist") || "No items in your wishlist"}
        </Text>

        <Text
          className="text-[20px] font-normal text-[#727783] font-['Inter'] leading-[28px] text-center max-w-[460px] mb-8"
        >
          {t("saveYourFavoriteCakes") || "Save your favorite cakes to buy them later."}
        </Text>

        <button
          className="px-8 py-3 rounded-full text-[white] text-[16px] font-semibold font-['Unbounded'] tracking-wide cursor-pointer transition-opacity hover:opacity-90 active:scale-95"
          style={{ backgroundColor: "#7B1A3C" }}
          onClick={() => navigate("/all-products")}
        >
          {t("browseCakes") || "Browse Cakes"}
        </button>
      </div>
    );
  }

  if (apiLoadStatus) {
    return (
      <div className="w-full">
        <Text className="font-['Inter'] text-2xl font-bold text-black-900 mb-6">
          {t("wishlist") || "Wishlist"}
        </Text>
        <div className="grid grid-cols-3 sm:grid-cols-2 xs:grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-gray-100 rounded-xl animate-pulse h-[280px]"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Text className="font-['Inter'] text-2xl font-bold text-black-900 mb-6 sm:text-[20px]">
        {t("wishlist") || "Wishlist"}
      </Text>

      <div className="grid grid-cols-3 md:grid-cols-3 sm:grid-cols-2 xs:grid-cols-2 gap-4 sm:gap-3 w-full">
        {wishlistItems.map((item, index) => {
          const product = item?.product;

          if (!product) return null;

          const productName = `${CUSTOM.getdataByLangKey(
            product?.productLocales,
            "",
            "name"
          )}${
            product?.productUoms?.[0]?.name
              ? ` - ${product.productUoms[0].name}`
              : ""
          }`;

          const sellingPrice =
            product?.productUoms?.[0]?.sellingPrice || item?.sellingPrice || 0;
          const actualPrice =
            product?.productUoms?.[0]?.actualPrice || item?.actualPrice || 0;
          const productImage = CUSTOM.getImage(
            product?.productUoms?.[0]?.productUomImages?.[0]?.image ||
              product?.imageUrl ||
              product?.image ||
              ""
          );

          return (
            <ProductCartCardBox
              key={`wishlist-${item?.id || index}`}
              productId={product?.id || item?.productId}
              className="bg-white-A700 flex flex-1 flex-col items-center justify-start p-[16px] rounded-lg w-full"
              productimage={productImage}
              isRecommended={product?.isRecommended}
              isBestSeller={product?.isBestseller}
              productRatingBox={!!product?.averageRating}
              productrating={`${product?.averageRating || 0} | ${
                product?.ratingCount || 0
              }`}
              productName={productName}
              discountedPrice={<>{sellingPrice}</>}
              originalPrice={
                !actualPrice || sellingPrice === actualPrice ? "" : actualPrice
              }
              buttonAddToCart
              slug={product?.slug}
              uomData={product?.productUoms?.[0]}
              cartData={cartData}
              isWishlist={wishlistIds.has(product?.id || item?.productId)}
              onWishlistToggle={handleWishlistToggle}
            />
          );
        })}
      </div>

      <style jsx>{`
        @media (max-width: 480px) {
          .xs\\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
      `}</style>
    </div>
  );
};

export default WishList;